import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  cancelCampaign,
  pauseCampaign,
  retryFailedRecipients,
  startCampaign,
  subscribeCampaign,
  subscribeRecipients,
} from '../services/campaigns';
import type {
  Campaign,
  CampaignStatus,
  Recipient,
  RecipientStatus,
} from '../../functions/src/shared/types';
import { formatTimestamp, recipientStatusColor, statusColor } from '../lib/format';

const FILTERS: Array<{ value: RecipientStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'complained', label: 'Complaints' },
  { value: 'suppressed', label: 'Suppressed' },
];

/** States where nothing is mid-send, so requeueing failed rows is safe. */
const RETRYABLE_STATUSES: CampaignStatus[] = ['paused', 'completed', 'canceled', 'failed'];

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="h6" component="p">
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}

export default function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<RecipientStatus | 'all'>('all');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!campaignId) return;
    const unsubscribeCampaign = subscribeCampaign(
      campaignId,
      setCampaign,
      (err) => setError(err.message),
    );
    const unsubscribeRecipients = subscribeRecipients(
      campaignId,
      setRecipients,
      (err) => setError(err.message),
    );
    return () => {
      unsubscribeCampaign();
      unsubscribeRecipients();
    };
  }, [campaignId]);

  const visible = useMemo(
    () => (filter === 'all' ? recipients : recipients.filter((r) => r.status === filter)),
    [recipients, filter],
  );

  const problems = useMemo(
    () => recipients.filter((r) => r.lastError).slice(0, 25),
    [recipients],
  );

  if (error) return <Alert severity="error">{error}</Alert>;

  if (!campaign) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label="Loading campaign" />
      </Box>
    );
  }

  const stats = campaign.stats;
  const progress = stats.total > 0 ? ((stats.total - stats.queued) / stats.total) * 100 : 0;

  async function run(action: (id: string) => Promise<void>) {
    if (!campaignId) return;
    setBusy(true);
    setNotice(null);
    try {
      await action(campaignId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  async function retryFailed(id: string) {
    const count = await retryFailedRecipients(id);
    setNotice(
      count > 0
        ? `${count} failed recipient${count === 1 ? '' : 's'} requeued. Press Resume to send.`
        : 'No failed recipients to retry.',
    );
  }

  return (
    <Stack spacing={4}>
      <Box>
        <Link component={RouterLink} to="/" variant="body2">
          ← All campaigns
        </Link>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          sx={{ mt: 1 }}
        >
          <Box>
            <Typography variant="h5" component="h1">
              {campaign.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {campaign.subject}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={campaign.status} color={statusColor(campaign.status)} />
            {campaign.status === 'sending' && (
              <Button disabled={busy} onClick={() => void run(pauseCampaign)}>
                Pause
              </Button>
            )}
            {campaign.status === 'paused' && (
              <Button variant="contained" disabled={busy} onClick={() => void run(startCampaign)}>
                Resume
              </Button>
            )}
            {stats.failed > 0 && RETRYABLE_STATUSES.includes(campaign.status) && (
              <Button disabled={busy} onClick={() => void run(retryFailed)}>
                Retry failed
              </Button>
            )}
            {(campaign.status === 'sending' || campaign.status === 'paused') && (
              <Button color="error" disabled={busy} onClick={() => void run(cancelCampaign)}>
                Cancel
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {!campaign.fromIsVerified && campaign.status !== 'draft' && (
        <Alert severity="info">
          Sending from the lodge's default address with {campaign.fromEmail} in Reply-To —{' '}
          {campaign.fromEmail.split('@')[1]} is not a verified sending domain in Postmark.
        </Alert>
      )}

      {campaign.lastError && <Alert severity="warning">{campaign.lastError}</Alert>}

      {notice && (
        <Alert severity="info" role="status" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          aria-label="Campaign progress"
          sx={{ height: 10, borderRadius: 5 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {stats.total - stats.queued} of {stats.total} processed
          {campaign.status === 'sending' &&
            ` · sending ${campaign.sendsPerMinute}/minute, up to ${campaign.dailyCap}/day`}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {[
          { label: 'Sent', value: stats.sent },
          { label: 'Queued', value: stats.queued },
          { label: 'Opened', value: stats.opened },
          { label: 'Clicked', value: stats.clicked },
          { label: 'Bounced', value: stats.bounced },
          { label: 'Failed', value: stats.failed },
          { label: 'Complaints', value: stats.complained },
          { label: 'Suppressed', value: stats.suppressed },
        ].map((tile) => (
          <Grid key={tile.label} size={{ xs: 6, sm: 3 }}>
            <StatTile label={tile.label} value={tile.value} />
          </Grid>
        ))}
      </Grid>

      {problems.length > 0 && (
        <Box>
          <Typography variant="subtitle1" component="h2" gutterBottom>
            Errors
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell component="th" scope="col">Email</TableCell>
                  <TableCell component="th" scope="col">Status</TableCell>
                  <TableCell component="th" scope="col">Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {problems.map((recipient) => (
                  <TableRow key={recipient.id}>
                    <TableCell>{recipient.email}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={recipient.status}
                        color={recipientStatusColor(recipient.status)}
                      />
                    </TableCell>
                    <TableCell>{recipient.lastError}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="subtitle1" component="h2">
            Recipients
          </Typography>
          <TextField
            select
            size="small"
            label="Filter"
            value={filter}
            onChange={(event) => setFilter(event.target.value as RecipientStatus | 'all')}
            sx={{ minWidth: 180 }}
          >
            {FILTERS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ minHeight: 44 }}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
          <Table size="small">
            <caption style={{ captionSide: 'bottom', padding: '8px 16px' }}>
              Showing {visible.length} of {stats.total} recipients. Large lists are capped at 500
              rows in this view; the counters above cover the whole campaign.
            </caption>
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">Email</TableCell>
                <TableCell component="th" scope="col">Status</TableCell>
                <TableCell component="th" scope="col">Sent</TableCell>
                <TableCell component="th" scope="col">Opened</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((recipient) => (
                <TableRow key={recipient.id}>
                  <TableCell>{recipient.email}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={recipient.status}
                      color={recipientStatusColor(recipient.status)}
                    />
                  </TableCell>
                  <TableCell>{formatTimestamp(recipient.sentAt)}</TableCell>
                  <TableCell>{formatTimestamp(recipient.openedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );
}
